import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { stripHtml, useSeo } from "../hooks/useSeo";
import { displayDates, focalToPosition } from "../utils/dates";
import { ExhibitionArchCard } from "../components/ExhibitionArchCard";
import { ExhibitionGallery } from "../components/ExhibitionGallery";
import { Reveal } from "../components/motion/Reveal";
import { Parallax } from "../components/motion/Parallax";
import { CircledArrow, SunIcon } from "../components/brand/icons";
import NotFoundPage from "./NotFoundPage";

export default function ExhibitionDetailPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [exhibition, setExhibition] = useState(null);
  const [more, setMore] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setExhibition(null);
    setMissing(false);
    const preview = params.get("preview") === "1" ? "?preview=1" : "";
    api
      .get(`/exhibitions/${slug}${preview}`)
      .then((res) => setExhibition(res.data))
      .catch(() => setMissing(true));
    api
      .get(`/exhibitions/${slug}/more`)
      .then((res) => setMore(res.data || []))
      .catch(() => {});
  }, [slug, params]);

  const isExternal = exhibition?.button_url && /^https?:\/\//.test(exhibition.button_url);

  useSeo(
    exhibition
      ? {
          title:
            exhibition.seo_title ||
            `${exhibition.title} at ${exhibition.gallery}, ${exhibition.city} | Pantheon Art`,
          description: exhibition.seo_description || stripHtml(exhibition.body).slice(0, 155),
          image: exhibition.share_image || exhibition.hero_image || exhibition.card_image,
          path: `/exhibitions/${exhibition.slug}`,
          noindex: exhibition.is_draft_preview,
          jsonLd: exhibition.start_date
            ? {
                "@context": "https://schema.org",
                "@type": "Event",
                name: exhibition.title,
                startDate: exhibition.start_date,
                ...(exhibition.end_date ? { endDate: exhibition.end_date } : {}),
                location: {
                  "@type": "Place",
                  name: exhibition.gallery,
                  address: [exhibition.city, exhibition.country].filter(Boolean).join(", "),
                },
                organizer: { "@type": "Organization", name: "Pantheon Art", url: window.location.origin },
                url: `${window.location.origin}/exhibitions/${exhibition.slug}`,
              }
            : undefined,
        }
      : {}
  );

  if (missing) return <NotFoundPage />;
  if (!exhibition) return <div className="min-h-screen" data-testid="exhibition-loading" />;

  return (
    <div data-testid="exhibition-detail-page">
      {exhibition.is_draft_preview && (
        <div className="fixed inset-x-0 top-0 z-[60] bg-charcoal px-5 py-2 text-center font-condensed text-xs font-semibold uppercase tracking-[0.14em] text-offwhite" data-testid="draft-preview-banner">
          Draft preview — this exhibition is not published
        </div>
      )}

      <section data-hero className="relative h-[100svh] overflow-hidden bg-charcoal" data-testid="exhibition-hero">
        <Parallax offset={50} className="absolute inset-0">
          <div className="h-[115%] w-full overflow-hidden">
            <img
              src={exhibition.hero_image}
              alt={exhibition.hero_alt || exhibition.title}
              style={{ objectPosition: focalToPosition(exhibition.hero_focal) }}
              className="kenburns h-full w-full object-cover"
            />
          </div>
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-charcoal/20" aria-hidden="true" />
        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-offwhite" data-testid="scroll-cue">
          <span className="scroll-cue-line block h-12 w-px bg-offwhite" />
          <SunIcon variant="offwhite" className="h-5 w-5" />
        </div>
      </section>

      <section className="hairline-t grid gap-12 px-5 py-16 lg:grid-cols-2 lg:gap-0 lg:px-10 lg:py-24" data-testid="exhibition-body">
        <div className="lg:pr-14">
          <Reveal>
            <h1 className="h-condensed text-[clamp(2.5rem,5.5vw,4.75rem)]">{exhibition.title}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <dl className="mt-10 space-y-6">
              <div>
                <dt className="eyebrow text-[0.65rem] opacity-60">Gallery</dt>
                <dd className="h-condensed mt-1.5 text-xl">{exhibition.gallery}</dd>
              </div>
              <div>
                <dt className="eyebrow text-[0.65rem] opacity-60">Location</dt>
                <dd className="h-condensed mt-1.5 text-xl">
                  {[exhibition.city, exhibition.country].filter(Boolean).join(", ")}
                </dd>
              </div>
              <div>
                <dt className="eyebrow text-[0.65rem] opacity-60">Dates</dt>
                <dd className="h-condensed mt-1.5 text-xl">
                  {displayDates(exhibition)}
                </dd>
              </div>
            </dl>
          </Reveal>
        </div>
        <div className="hairline-t pt-12 lg:border-l lg:border-t-0 lg:py-0 lg:pl-14" style={{ borderColor: "var(--charcoal)" }}>
          <Reveal delay={0.15}>
            <div className="rich-text" data-testid="exhibition-body-text" dangerouslySetInnerHTML={{ __html: exhibition.body }} />
          </Reveal>
          {exhibition.button_label && exhibition.button_url && (
            <Reveal delay={0.25}>
              <a
                href={exhibition.button_url}
                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid="exhibition-cta-button"
                className="link-arrow mt-10 text-xs"
              >
                {exhibition.button_label}
                <CircledArrow className="h-5 w-5" />
              </a>
            </Reveal>
          )}
        </div>
      </section>

      <ExhibitionGallery images={exhibition.gallery_images} />

      {more.length > 0 && (
        <section className="hairline-t px-5 py-16 lg:px-10 lg:py-24" data-testid="more-exhibitions">
          <Reveal>
            <h2 className="h-condensed text-2xl lg:text-3xl">More Exhibitions</h2>
          </Reveal>
          <div className="mt-10 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {more.map((ex, i) => (
              <Reveal key={ex.id || ex.slug} delay={i * 0.1}>
                <ExhibitionArchCard exhibition={ex} showMeta />
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
